import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Loader2, Send, Sparkles, Trash2 } from 'lucide-react';
import { useData } from '../App';
import { Button, Card } from '../components/UI';
import { AiAnalysis } from '../types';
import { api } from '../services/api';
import { confirmDialog } from '../components/Dialogs';

// Вкладка «Анализ»: Claude разбирает приходы и расходы и даёт рекомендации; можно задавать вопросы в продолжение разговора

const inline = (text: string, key: string): React.ReactNode[] =>
    text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => p.startsWith('**') && p.endsWith('**') && p.length > 4
        ? <strong key={`${key}-${i}`}>{p.slice(2, -2)}</strong>
        : p.startsWith('`') && p.endsWith('`') && p.length > 2 ? <code key={`${key}-${i}`} className="bg-slate-100 px-1 rounded">{p.slice(1, -1)}</code> : p);

/** Небольшой Markdown: заголовки, списки, таблицы, жирный — ответы Claude. */
export const Markdown: React.FC<{ text: string }> = ({ text }) => {
    const out: React.ReactNode[] = [];
    const lines = text.replace(/\r/g, '').split('\n');
    let i = 0;
    while (i < lines.length) {
        const l = lines[i];
        const k = `l${i}`;
        if (!l.trim()) { i++; continue; }
        const h = l.match(/^(#{1,4})\s+(.*)/);
        if (h) {
            const cls = h[1].length <= 2 ? 'text-base font-semibold mt-4 mb-1 text-slate-900' : 'text-sm font-semibold mt-3 mb-1 text-slate-800';
            out.push(<div key={k} className={cls}>{inline(h[2], k)}</div>); i++; continue;
        }
        if (/^\s*\|/.test(l)) {
            const rows: string[][] = [];
            while (i < lines.length && /^\s*\|/.test(lines[i])) {
                if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[i])) rows.push(lines[i].trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim()));
                i++;
            }
            out.push(<div key={k} className="overflow-x-auto my-2"><table className="text-xs border border-slate-200">
                <thead className="bg-slate-50"><tr>{rows[0]?.map((c, j) => <th key={j} className="px-2 py-1 text-left font-medium border-b border-slate-200">{inline(c, `${k}h${j}`)}</th>)}</tr></thead>
                <tbody>{rows.slice(1).map((r, ri) => <tr key={ri} className="border-t border-slate-100">{r.map((c, j) => <td key={j} className="px-2 py-1 align-top">{inline(c, `${k}${ri}${j}`)}</td>)}</tr>)}</tbody>
            </table></div>);
            continue;
        }
        if (/^\s*([-*•]|\d+[.)])\s+/.test(l)) {
            const ordered = /^\s*\d+[.)]/.test(l);
            const items: string[] = [];
            while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
                i++;
                while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) { items[items.length - 1] += ' ' + lines[i].trim(); i++; }
            }
            const Tag = ordered ? 'ol' : 'ul';
            out.push(<Tag key={k} className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 my-1`}>{items.map((it, j) => <li key={j}>{inline(it, `${k}${j}`)}</li>)}</Tag>);
            continue;
        }
        if (/^---+$/.test(l.trim())) { out.push(<hr key={k} className="my-3 border-slate-200" />); i++; continue; }
        const para: string[] = [];
        while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|\s*\||\s*([-*•]|\d+[.)])\s+)/.test(lines[i])) { para.push(lines[i]); i++; }
        out.push(<p key={k} className="my-1.5">{inline(para.join(' '), k)}</p>);
    }
    return <div className="text-sm leading-relaxed text-slate-700">{out}</div>;
};

const when = (iso: string) => new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const rootOf = (a: AiAnalysis, byId: Record<string, AiAnalysis>) => { let x = a; while (x.parentId && byId[x.parentId]) x = byId[x.parentId]; return x.id; };

const Analysis: React.FC = () => {
    const { showNotification } = useData();
    const [items, setItems] = useState<AiAnalysis[]>([]);
    const [status, setStatus] = useState<{ enabled: boolean; model: string } | null>(null);
    const [thread, setThread] = useState<string | null>(null);  // id корня разговора; null — новый
    const [question, setQuestion] = useState('');
    const [busy, setBusy] = useState(false);
    const bottom = useRef<HTMLDivElement>(null);

    const load = useCallback(async () => setItems(await api.aiList()), []);
    useEffect(() => { api.aiStatus().then(setStatus).catch(() => setStatus({ enabled: false, model: '' })); load().catch(() => {}); }, [load]);
    const pending = items.some(a => a.pending);
    useEffect(() => {
        if (!pending) return;
        const t = setInterval(() => load().catch(() => {}), 4000);
        return () => clearInterval(t);
    }, [pending, load]);

    const byId = Object.fromEntries(items.map(a => [a.id, a]));
    const roots = items.filter(a => !a.parentId || !byId[a.parentId]);
    const current = thread ? items.filter(a => rootOf(a, byId) === thread).sort((a, b) => a.createdAt.localeCompare(b.createdAt)) : [];
    useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [current.length, pending]);

    const ask = async (q: string | null, fresh = false) => {
        setBusy(true);
        try {
            const last = current[current.length - 1];
            const a = await api.aiAsk(q, thread && !fresh ? last?.id || thread : null);
            setItems(xs => [a, ...xs]);
            if (!thread || fresh) setThread(a.id);
            setQuestion('');
        } catch (e: any) { showNotification(e?.message || 'Ошибка', 'error'); }
        finally { setBusy(false); }
    };
    const remove = async (id: string) => {
        if (!(await confirmDialog('Удалить этот разговор?'))) return;
        const ids = items.filter(a => rootOf(a, byId) === id).map(a => a.id).reverse();
        for (const x of ids) await api.aiDelete(x).catch(() => {});
        if (thread === id) setThread(null);
        load();
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><Sparkles className="text-violet-600" size={18} /> Анализ ИИ</h2>
                <Button onClick={() => ask(null, true)} disabled={busy || pending || !status?.enabled} className="ml-auto">
                    {pending ? <Loader2 size={16} className="animate-spin mr-2" /> : <Bot size={16} className="mr-2" />} Новый анализ
                </Button>
            </div>
            {status && !status.enabled && <Card className="bg-amber-50 text-sm">Анализ ИИ не настроен: на сервере нет ключа Claude API.</Card>}
            <p className="text-xs text-slate-500">Claude ({status?.model || '…'}) получает обороты, остатки, налоги, выплаты, выводы и сверку счетов по всем организациям —
                без ИНН и номеров счетов — и пишет рекомендации: снятие наличных, переводы самозанятым, налоги, ошибки учёта. Анализ занимает 1–3 минуты.
                Рекомендации ИИ — подсказка, решения проверяйте с бухгалтером.</p>
            <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
                <Card className="p-2 h-fit">
                    <button onClick={() => setThread(null)} className={`w-full text-left px-2 py-1.5 rounded text-sm ${!thread ? 'bg-violet-50 text-violet-700 font-medium' : 'hover:bg-slate-50'}`}>+ Новый разговор</button>
                    {roots.map(r => (
                        <div key={r.id} className={`group flex items-start gap-1 px-2 py-1.5 rounded text-sm cursor-pointer ${thread === r.id ? 'bg-violet-50 text-violet-700' : 'hover:bg-slate-50'}`}
                            onClick={() => setThread(r.id)}>
                            <div className="flex-1 min-w-0">
                                <div className="truncate">{r.question || 'Общий анализ'}</div>
                                <div className="text-[11px] text-slate-400">{when(r.createdAt)}{items.some(a => a.pending && rootOf(a, byId) === r.id) ? ' · думает…' : ''}</div>
                            </div>
                            <button onClick={e => { e.stopPropagation(); remove(r.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" title="Удалить"><Trash2 size={14} /></button>
                        </div>))}
                    {roots.length === 0 && <div className="text-xs text-slate-400 px-2 py-2">Анализов ещё не было</div>}
                </Card>
                <div className="space-y-3 min-w-0">
                    {!thread && (
                        <Card className="text-sm text-slate-600">
                            Нажмите «Новый анализ» — общий разбор всех приходов и расходов. Или задайте вопрос, например:
                            <div className="flex flex-wrap gap-2 mt-2">
                                {['Сколько и из какого ИП можно снять наличными в этом месяце?', 'Есть ли риски по выплатам самозанятым?',
                                    'Почему не сходятся остатки по банку?', 'Какие налоги платить в ближайший месяц?'].map(q => (
                                    <button key={q} onClick={() => setQuestion(q)} className="px-2 py-1 rounded-full border border-slate-300 text-xs hover:bg-slate-50">{q}</button>))}
                            </div>
                        </Card>)}
                    {current.map(a => (
                        <div key={a.id} className="space-y-2">
                            <div className="flex justify-end"><div className="max-w-[85%] bg-violet-600 text-white rounded-lg px-3 py-2 text-sm">{a.question || 'Общий анализ и рекомендации'}</div></div>
                            <Card className="bg-white">
                                {a.pending ? <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Claude анализирует данные…</div>
                                    : a.error ? <div className="text-sm text-red-600">Ошибка: {a.error}</div>
                                        : <Markdown text={a.answer || ''} />}
                                {!a.pending && a.outputTokens != null && <div className="text-[11px] text-slate-400 mt-2">{when(a.createdAt)} · {a.model} · {a.inputTokens}+{a.outputTokens} токенов</div>}
                            </Card>
                        </div>))}
                    <div ref={bottom} />
                    <form onSubmit={e => { e.preventDefault(); if (question.trim()) ask(question.trim()); }} className="flex gap-2 sticky bottom-2">
                        <textarea value={question} onChange={e => setQuestion(e.target.value)} rows={2} placeholder={thread ? 'Уточняющий вопрос…' : 'Ваш вопрос по финансам…'}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (question.trim()) ask(question.trim()); } }}
                            className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm bg-white resize-none" />
                        <Button type="submit" disabled={busy || pending || !question.trim() || !status?.enabled}><Send size={16} /></Button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Analysis;
